import { useDispatch, useSelector } from "react-redux";
import { ProModal } from "pro-features/components/Modals/src/ProModal";
import type { ContactExportPreview } from "../../../types/common";
import {
    selectCannotBeExportedContactsPreview,
    selectExistAsLeadExportContactsPreview,
    selectExportContactsPreviewCount,
    selectUnmatchedExportContactsPreview,
} from "../../../store/selectors";
import { contactExportPreviewChanged, contactExportPreviewDeleted } from "../../../store/action-creators";
import { ContactExportPreviewItem } from "./ContactExportPreviewItem/ContactExportPreviewItem";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";
import trackingService from "../../../services/tracking/trackingService";
import {
    StyledModalDescription,
    StyledContactsListHeader,
    StyledContactsListHeading,
    StyledContactsList,
    modalStyles,
} from "./styles";
import { StyledModalHeader } from "./shared/styles";
import { ExportPreviewEmptyState } from "./shared/ExportPreviewEmptyState";
import { ExportPreviewFooter } from "./shared/ExportPreviewFooter";
import type { Integration } from "app/pages/sales-intelligence/sub-modules/management/types";
import { CONTACTS_EXPORT_PREVIEW_MODAL_CLASS } from "./consts";

type ContactsExportPreviewModalProps = {
    integrationName: Integration;
    onExport(): void;
    onCancel(): void;
    isOpen: boolean;
};

const CONTACTS_EXPORT_PREVIEW_MODAL_OVERLAY_CLASS = "contacts-export-preview-modal-overlay";

const ContactsExportPreviewModal = (props: ContactsExportPreviewModalProps) => {
    const { integrationName, onExport, onCancel, isOpen } = props;

    const translate = useTranslation();

    const dispatch = useDispatch();

    const unmatchedContacts = useSelector(selectUnmatchedExportContactsPreview);
    const existAsLeadContacts = useSelector(selectExistAsLeadExportContactsPreview);
    const cannotBeExportedContacts = useSelector(selectCannotBeExportedContactsPreview);
    const unMatchedNotExistedAsLeadsContacts = unmatchedContacts.filter((contact) => !contact.isExistAsLead);
    const newlyMatchedContacts = unMatchedNotExistedAsLeadsContacts.filter((contact) => !!contact.accountId);
    const newlyUnmatchedContacts = unMatchedNotExistedAsLeadsContacts.filter((contact) => !contact.accountId);

    const total = useSelector(selectExportContactsPreviewCount);
    const unmatchedCount = unmatchedContacts.length;
    const matchedCount = total - unmatchedCount;
    const existAsLeadCount = existAsLeadContacts.length;
    const cannotBeExportedCount = cannotBeExportedContacts.length;
    const contactsToExportCount = total - cannotBeExportedCount + newlyMatchedContacts.length;

    const handleDeleteContact = (contactId: string) => {
        trackingService.trackRemoveContactFromExport(integrationName);
        dispatch(contactExportPreviewDeleted(contactId));
        if (total === 1) {onCancel();}
    };

    const handleContactChange = (contact: ContactExportPreview, searchedLabel?: string) => {
        const sameCompanyContacts = newlyUnmatchedContacts.filter(
            (contactItem) => contactItem.accountName === contact.accountName,
        );
        sameCompanyContacts.map((contactItem) => {
            const updatedContact = {
                ...contactItem,
                accountId: contact.accountId,
            };
            if (searchedLabel) {Object.assign(updatedContact, { searchedCompanyLabel: searchedLabel });}
            dispatch(contactExportPreviewChanged(updatedContact));
        });
        dispatch(contactExportPreviewChanged(contact));
    };

    const handleExport = () => {
        trackingService.trackApproveExportToCrm(integrationName, total);
        onExport();
    };

    const isExportButtonTooltipShown = unMatchedNotExistedAsLeadsContacts.some((contact) => !contact.accountId);
    const isExportButtonDisabled = isExportButtonTooltipShown || total === existAsLeadContacts.length;

    return (
        <ProModal
            showCloseIcon={false}
            shouldCloseOnOverlayClick={false}
            isOpen={isOpen}
            customStyles={modalStyles}
            className={CONTACTS_EXPORT_PREVIEW_MODAL_CLASS}
            overlayClassName={CONTACTS_EXPORT_PREVIEW_MODAL_OVERLAY_CLASS}
        >
            <StyledModalHeader>{translate("si.advanced_search.contacts_export_preview_modal.title")}</StyledModalHeader>
            {unmatchedCount || existAsLeadCount ? (
                <>
                    <StyledModalDescription
                        dangerouslySetInnerHTML={{
                            __html: translate(
                                `si.advanced_search.contacts_export_preview_modal.description.${integrationName}`,
                                { matchedCount, total },
                            ),
                        }}
                    />
                    <StyledContactsListHeader>
                        <StyledContactsListHeading>
                            {translate("si.advanced_search.contacts_export_preview_modal.contact_name")}
                            {` (${cannotBeExportedCount})`}
                        </StyledContactsListHeading>
                        <StyledContactsListHeading>
                            {translate(
                                `si.advanced_search.contacts_export_preview_modal.account_name.${integrationName}`,
                            )}
                        </StyledContactsListHeading>
                    </StyledContactsListHeader>
                    <StyledContactsList>
                        {unMatchedNotExistedAsLeadsContacts.map((contact: ContactExportPreview) => (
                            <ContactExportPreviewItem
                                key={contact.contactId}
                                contact={contact}
                                integrationName={integrationName}
                                onChange={handleContactChange}
                                onDelete={handleDeleteContact}
                            />
                        ))}
                        {existAsLeadContacts.map((contact: ContactExportPreview) => (
                            <ContactExportPreviewItem
                                key={contact.contactId}
                                contact={contact}
                                integrationName={integrationName}
                                onChange={handleContactChange}
                                onDelete={handleDeleteContact}
                            />
                        ))}
                    </StyledContactsList>
                </>
            ) : (
                <ExportPreviewEmptyState
                    title={translate("si.advanced_search.contacts_export_preview_modal.empty_list_title")}
                    description={translate("si.advanced_search.contacts_export_preview_modal.empty_list_description", {
                        count: contactsToExportCount,
                    })}
                    subtitle={translate("si.advanced_search.contacts_export_preview_modal.empty_list_subtitle")}
                />
            )}
            <ExportPreviewFooter
                countLabel={translate("si.advanced_search.contacts_export_preview_modal.export_count")}
                count={contactsToExportCount}
                isExportDisabled={isExportButtonDisabled}
                isTooltipShown={isExportButtonTooltipShown}
                tooltipContent={translate(
                    `si.advanced_search.contacts_export_preview_modal.export_button_disabled.${integrationName}`,
                )}
                onCancel={onCancel}
                onExport={handleExport}
            />
        </ProModal>
    );
};

export default ContactsExportPreviewModal;
